import {
  Fab, Paper, TextField, Typography,
} from '@mui/material';
import * as React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';
import { useFormik } from 'formik';

import useLocaleUrl from '../../hooks/useLocaleUrl';

const useStyles = makeStyles()(() => ({
  paper: {
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: 50,
  },

  errorText: {
    color: '#f00',
    marginTop: 20,
    display: 'block',
  },

  topMargin: {
    marginTop: 20,
  },

  captchaContainer: {
    display: 'flex',
    height: 75,
    justifyContent: 'center',
  },
}));

export interface RedeemCodeFromProps {
  onChangeCode: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  onHandleSubmit: () => void;
  errorText: string;
  submitted: boolean;
  code?: string;
}

const GIFT_CODE_STRING_LENGTH = 19;

const RedeemCodeForm: React.FC<RedeemCodeFromProps> = ({
  onChangeCode,
  onHandleSubmit,
  errorText,
  submitted,
  code,
}) => {
  const RECAPTCHA_SITEKEY = '6LfsUFgdAAAAAK0o41m-TTNvWJtUadFam2QdgPDt';
  // const TAG = '[RedeemCodeForm]';
  const { classes, cx } = useStyles();
  const { t } = useTranslation(['common']);
  const [captcha, setCaptcha] = React.useState('');
  const localeUrl = useLocaleUrl();
  const GIFT_CODE_REGEX = /^((\d{4}-\d{4}-\d{4}-\d{4}$)|($))/;
  const validationSchema = Yup.object({
    giftCode: Yup.string()
      .matches(GIFT_CODE_REGEX, t('redeem.formatError'))
      .required(t('home.input.youtube_url')),
  });

  const formik = useFormik({
    initialValues: {
      giftCode: code || '',
    },
    validationSchema,
    onSubmit: () => onHandleSubmit(),
  });
  const isGiftCode = (value: string) => {
    const regex = /^((\d{4}-\d{4}-\d{4}-\d{4}$)|($))/;
    const match = value.match(regex);
    return !!(match && (match[0].length === GIFT_CODE_STRING_LENGTH || match[0].length === 0));
  };

  const canContinue = () =>
    submitted || captcha.length <= 0 || formik.values.giftCode.length !== GIFT_CODE_STRING_LENGTH;

  return (
    <Paper className={classes.paper} elevation={0}>
      <Typography variant="subtitle2">
        {t(
          'Enter the PIN on the back of your gift card, or enter the premium code from your in-store receipt.',
        )}
      </Typography>
      <form onSubmit={formik.handleSubmit}>
        <TextField
          className={classes.topMargin}
          fullWidth
          name="giftCode"
          type="text"
          variant="outlined"
          error={!!formik.errors.giftCode}
          label={t('redeem.enter')}
          value={formik.values.giftCode}
          helperText={formik.errors.giftCode}
          onChange={(e: any) => {
            formik.handleChange(e);
            if (onChangeCode) onChangeCode(e);
          }}
        />
        <div className={cx(classes.topMargin, classes.captchaContainer)}>
          {isGiftCode(formik.values.giftCode) && formik.values.giftCode.length > 0 && (
            <ReCAPTCHA
              onChange={(val: string) => {
                if (val) {
                  setCaptcha(val);
                } else {
                  setCaptcha('');
                }
              }}
              sitekey={RECAPTCHA_SITEKEY}
            />
          )}
        </div>
        {errorText && (
          <Typography align="center" variant="caption" className={classes.errorText}>
            {errorText}
          </Typography>
        )}
        <Fab
          type="submit"
          variant="extended"
          size="medium"
          color="primary"
          disabled={canContinue()}
          className={classes.topMargin}
        >
          {t('redeem.submit_btn')}
        </Fab>
        <br />
        <br />
        <a href={localeUrl('/#section-pricing')}>{t('redeem.payment_option')}</a>
        <br />
        <br />
        <a href="https://klang.io/terms/">{t('redeem.terms_and_cond')}</a>
      </form>
    </Paper>
  );
};

export default RedeemCodeForm;
